<template>
  <div class="connection-access row">
    <q-input
      :model-value="placeholderLabel"
      no-error-icon
      label="Access"
      stack-label
      :disable="true"
      class="flex-1 connection-access-label"
    >
      <template v-slot:prepend>
        <runai-svg-icon class="q-mt-md" name="user-lock" />
      </template>
    </q-input>
    <span>
      <q-btn
        class="edit-btn"
        round
        flat
        size="sm"
        icon="fa-solid fa-pen"
        @click="showModal = true"
        aid="edit-access-button"
        :disabled="disabled"
      ></q-btn>
      <q-tooltip v-if="accessBtnTooltip">{{ accessBtnTooltip }}</q-tooltip>
    </span>

    <connection-access-modal
      v-if="showModal"
      :default-user="userEmail"
      :tool-name="toolName"
      :access="access"
      :multi-users="isMultiUsers"
      @close="onClose"
      @save="saveAccess"
      :enable-public-access="enablePublicAccess"
      :connection-type="connectionType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

// cmps
import RunaiSvgIcon from "@/components/common/runai-svg-icon/runai-svg-icon.vue";
import { ConnectionAccessModal } from "./connection-access-modal";
import { type IConnectionAccess, EAccessOptions } from "./connection-access-modal/connection-access-modal.model";

// stores
import { useAuthStore } from "@/stores/auth.store";

export default defineComponent({
  components: {
    RunaiSvgIcon,
    ConnectionAccessModal,
  },
  emits: ["save"],
  props: {
    access: {
      type: Object as PropType<IConnectionAccess>,
      required: true,
    },
    toolName: {
      type: String as PropType<string>,
      required: true,
    },
    isMultiUsers: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    enablePublicAccess: {
      type: Boolean as PropType<boolean>,
      required: false,
    },
    connectionType: {
      type: String as PropType<"tool" | "endpoint">,
      required: true,
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    disabledTooltip: {
      type: String as PropType<string>,
      required: false,
    },
  },
  data() {
    return {
      authStore: useAuthStore(),
      placeholderLabel: "" as string,
      showModal: false as boolean,
      accessRoles: {} as IConnectionAccess,
    };
  },
  created() {
    this.accessRoles.authorizedGroups = this.access.authorizedGroups;
    this.accessRoles.authorizedUsers = this.access.authorizedUsers;
    this.accessRoles.accessOption = this.access.accessOption;
    this.updateLabel();
  },
  computed: {
    userEmail(): string {
      return this.authStore.userEmail;
    },
    accessBtnTooltip(): string | undefined {
      if (!this.disabled) return `Click to change who can access this ${this.connectionType}`;
      if (this.disabled && this.disabledTooltip) return this.disabledTooltip;

      return undefined;
    },
  },
  methods: {
    onClose(): void {
      this.showModal = false;
    },
    saveAccess(access: IConnectionAccess): void {
      this.showModal = false;
      this.accessRoles = access;

      this.$emit("save", access);
      this.$nextTick(() => {
        this.updateLabel();
      });
    },
    updateLabel(): void {
      if (this.accessRoles.authorizedGroups && this.isMultiUsers) {
        this.placeholderLabel = `Groups (${this.accessRoles.authorizedGroups.length})`;
        return;
      }

      const authorizedUsers: string[] | undefined | null = this.accessRoles.authorizedUsers;
      if (authorizedUsers) {
        this.placeholderLabel = this.isMultiUsers ? `Users (${authorizedUsers.length})` : `Private`;
      } else if (this.accessRoles.accessOption === EAccessOptions.PUBLIC && this.enablePublicAccess) {
        this.placeholderLabel = "Public";
      } else {
        this.placeholderLabel = "All authenticated users";
      }
    },
  },
});
</script>
<style lang="scss" scoped>
.connection-access {
  width: 100%;

  .connection-access-label {
    margin-bottom: 20px;
  }

  .edit-btn {
    color: $black-54;
    margin-top: 23px;
  }
}
</style>
